import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Select, Spin, Steps, Switch, message } from 'antd';
import * as claus from 'src/_shared/services/claus/endpoints';
import { graphql, parse } from 'src/_shared/services/utils.js';
import { listAtsEntries, readConfigFromAtsIntegration } from 'src/_shared/services/job-sync/jobSyncConfig';
import {
	STEP_ORDER,
	STEP_LABELS,
	NOT_FOUND_RE,
	CLAUS_CROSS_CUSTOMER_RE,
	assessTargetEntries,
	buildPromotedConfig,
	buildSeedCommand,
	findNameCollision,
} from 'src/_shared/services/promote/promote';
import { useAllCompanies } from '../hooks/useAllCompanies';
import { usePromoteFlow } from '../hooks/usePromoteFlow';

// Promote a validated integration to ANOTHER company (the test→prod cutover path): ensure the CLAUS
// org (create-with-id, org id == company id), duplicate the integration into it server-side
// (credentials included), copy its webhooks, and copy the job-sync config re-pointed at the target
// with mode forced to dry-run. Distinct from DuplicateIntegrationDialog on purpose — that one is a
// harmless same-company copy; this one is a cross-tenant write with one non-idempotent step.
const STEP_STATUS_TO_ANTD = { pending: 'wait', running: 'process', done: 'finish', failed: 'error', manual: 'finish' };

function downloadBlob(filename, text, mime) {
	const blob = new Blob([text], { type: mime });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

export default function PromoteIntegrationDialog({ sourceOrgId, sourceCompany, integration, onClose }) {
	const sourceCompanyId = sourceCompany?.id || sourceOrgId; // canonical: org id == company id
	const { companies, failed: companiesFailed } = useAllCompanies();
	const flow = usePromoteFlow();

	const [name, setName] = useState('');
	const [copyCredentials, setCopyCredentials] = useState(true);
	const [targetId, setTargetId] = useState(null);
	const [manualTargetName, setManualTargetName] = useState(''); // companies-load-failure fallback
	const [targetState, setTargetState] = useState(null); // { loading, orgStatus, ats, integrations }
	const [targetAtsName, setTargetAtsName] = useState(null);
	const [sourceAts, setSourceAts] = useState(null);
	const [sourceAtsName, setSourceAtsName] = useState(null);
	const [acks, setAcks] = useState({ overwrite: false, shadowed: false });
	const [enabling, setEnabling] = useState(false);

	// Fresh dialog per opened integration (destroyOnClose handles close; this handles re-open).
	useEffect(() => {
		if (!integration) return;
		setName(integration.name || '');
		setCopyCredentials(true);
		setTargetId(null);
		setManualTargetName('');
		setTargetState(null);
		setTargetAtsName(null);
		setAcks({ overwrite: false, shadowed: false });
		flow.reset();
		// flow is stable by usage (see useClaus.js note on react-hooks lint in this build)
	}, [integration]);

	// Source entries — which entry's config we're promoting (auto-pick the first that has one).
	useEffect(() => {
		if (!integration) return;
		let alive = true;
		(async () => {
			const res = await graphql({ input: { id: sourceCompanyId }, query: 'getATSSettingsByCompany' }, 'cognito');
			if (!alive) return;
			const ats = res === undefined ? undefined : parse(res?.atsIntegration) || [];
			setSourceAts(ats);
			const withConfig = listAtsEntries(ats || []).filter((e) => e.hasConfig);
			setSourceAtsName(withConfig[0]?.atsName || null);
		})();
		return () => {
			alive = false;
		};
	}, [integration, sourceCompanyId]);

	// Target pre-flight: CLAUS org existence, ATS entries (for the config landing spot), name collisions.
	useEffect(() => {
		if (!integration || !targetId) {
			setTargetState(null);
			setTargetAtsName(null);
			return;
		}
		let alive = true;
		(async () => {
			setTargetState({ loading: true });
			const [orgStatus, atsRes, intsRes] = await Promise.all([
				claus
					.getOrg(targetId)
					.then(() => 'exists')
					.catch((e) => (NOT_FOUND_RE.test(e.message || '') ? 'missing' : 'error')),
				graphql({ input: { id: targetId }, query: 'getATSSettingsByCompany' }, 'cognito'),
				claus.listIntegrations(targetId).catch(() => null),
			]);
			if (!alive) return;
			const ats = atsRes === undefined ? undefined : parse(atsRes?.atsIntegration) || [];
			setTargetState({ loading: false, orgStatus, ats, integrations: intsRes });
			const entries = listAtsEntries(ats || []);
			setTargetAtsName(entries.length === 1 ? entries[0].atsName : null);
			setAcks({ overwrite: false, shadowed: false });
		})();
		return () => {
			alive = false;
		};
	}, [integration, targetId]);

	const targetCompany = useMemo(() => {
		const fromList = (companies || []).find((c) => c.id === targetId);
		if (fromList) return fromList;
		return targetId ? { id: targetId, name: manualTargetName.trim() } : null;
	}, [companies, targetId, manualTargetName]);

	const assess = useMemo(
		() => (targetState?.ats !== undefined && targetState?.ats && targetAtsName ? assessTargetEntries(targetState.ats, targetAtsName) : null),
		[targetState, targetAtsName],
	);
	const collision = useMemo(
		() => (targetState?.integrations ? findNameCollision(targetState.integrations, name) : null),
		[targetState, name],
	);
	const targetEntries = useMemo(() => listAtsEntries(targetState?.ats || []), [targetState]);
	const sourceConfigEntries = useMemo(() => listAtsEntries(sourceAts || []).filter((e) => e.hasConfig), [sourceAts]);
	const targetAtsUnavailable = targetState && !targetState.loading && targetState.ats === undefined;

	const running = flow.phase === 'running';
	const done = flow.phase === 'done';
	const adopted = !!flow.ctx.newIntegrationId;

	const needOverwriteAck = !!assess?.overwrite;
	const needShadowAck = !!assess?.shadowed;
	const canRun =
		!!integration &&
		!!targetCompany?.id &&
		targetCompany.id !== sourceCompanyId &&
		!!name.trim() &&
		!running &&
		!done &&
		(adopted || !collision) &&
		(!companiesFailed || !!manualTargetName.trim() || targetState?.orgStatus === 'exists') &&
		(targetAtsUnavailable || !!targetAtsName) &&
		(!needOverwriteAck || acks.overwrite) &&
		(!needShadowAck || acks.shadowed);

	const seedCommand =
		targetCompany?.id && (targetAtsName || targetAtsUnavailable)
			? buildSeedCommand({ targetCompanyId: targetCompany.id, atsName: targetAtsName || '<target ATSName>' })
			: null;

	function startRun() {
		flow.run({
			sourceOrgId,
			sourceCompanyId,
			sourceAtsName,
			integration,
			targetCompany,
			name: name.trim(),
			copyCredentials,
			targetAtsName,
			acks,
		});
	}

	function downloadPromotedJson() {
		const sourceConfig = sourceAts ? readConfigFromAtsIntegration(sourceAts, sourceAtsName) : null;
		if (!sourceConfig || !targetCompany?.id) {
			message.error('No source config loaded to download.');
			return;
		}
		const promoted = buildPromotedConfig(sourceConfig, {
			targetCompanyId: targetCompany.id,
			targetAtsName: targetAtsName || sourceAtsName,
		});
		downloadBlob(`promoted-config-${targetCompany.id}.json`, JSON.stringify(promoted, null, 2), 'application/json');
	}

	function enableNow() {
		Modal.confirm({
			title: 'Enable the promoted integration?',
			content:
				'Enabling starts its sync schedule against the client’s live ATS. The copied job-sync config is in dry-run, so no ERIN jobs are written until you flip it to Live.',
			okText: 'Enable',
			okButtonProps: { danger: true },
			onOk: async () => {
				setEnabling(true);
				try {
					await claus.patchIntegration(targetCompany.id, flow.ctx.newIntegrationId, { enabled: true });
					message.success('Integration enabled');
				} catch (e) {
					message.error(e.message || 'Could not enable the integration.');
				} finally {
					setEnabling(false);
				}
			},
		});
	}

	const configFailed = flow.steps.config.status === 'failed';
	const crossCustomer = flow.steps.duplicate.error && CLAUS_CROSS_CUSTOMER_RE.test(flow.steps.duplicate.error);

	const footer = [
		<Button key="cancel" onClick={onClose} disabled={running}>
			{done ? 'Close' : 'Cancel'}
		</Button>,
		done ? (
			<Button key="done" type="primary" onClick={onClose}>
				Done
			</Button>
		) : (
			<Button key="run" type="primary" onClick={startRun} disabled={!canRun} loading={running}>
				{flow.phase === 'failed' ? 'Retry remaining steps' : 'Promote'}
			</Button>
		),
	];

	return (
		<Modal
			title={integration ? `Promote ${integration.name} to another company` : 'Promote'}
			open={!!integration}
			onCancel={running ? undefined : onClose}
			footer={footer}
			width={680}
			destroyOnClose
			maskClosable={false}
			wrapClassName="erin-integrations"
		>
			<p className="ei-promote-intro">
				Copies this CLAUS integration — credentials, field mappings, status mappings, input parameters, schedule —
				plus its webhooks and job-sync config to another company, creating the CLAUS organization there if needed
				(org id == company id). The copy arrives <b>disabled</b> and the config lands in <b>dry-run</b>.
			</p>

			<div className="ei-field">
				<label className="ei-field-label">Target company</label>
				{companiesFailed ? (
					<>
						<Input
							value={targetId || ''}
							onChange={(e) => setTargetId(e.target.value.trim() || null)}
							placeholder="Target company id (companies list failed to load)"
						/>
						<Input
							style={{ marginTop: 8 }}
							value={manualTargetName}
							onChange={(e) => setManualTargetName(e.target.value)}
							placeholder="Target company name (used if the CLAUS org must be created)"
						/>
					</>
				) : (
					<Select
						showSearch
						loading={companies === null}
						value={targetId}
						onChange={setTargetId}
						placeholder="Search companies by name or id…"
						style={{ width: '100%' }}
						optionFilterProp="label"
						filterOption={(input, option) =>
							`${option?.label || ''} ${option?.value || ''}`.toLowerCase().includes(input.toLowerCase())
						}
						options={(companies || [])
							.filter((c) => c.id !== sourceCompanyId)
							.map((c) => ({ value: c.id, label: c.name || c.id }))}
					/>
				)}
				<span className="ei-field-hint">
					For a same-company copy use Duplicate instead. The current company is excluded here.
				</span>
			</div>

			{targetState?.loading && (
				<div className="ei-promote-preflight">
					<Spin size="small" /> Checking the target…
				</div>
			)}

			{targetState && !targetState.loading && (
				<div className="ei-promote-preflight">
					{targetState.orgStatus === 'exists' && <span className="is-ok">✓ CLAUS organization exists</span>}
					{targetState.orgStatus === 'missing' && <span>· CLAUS organization will be created (id == company id)</span>}
					{targetState.orgStatus === 'error' && (
						<span className="is-warn">! Could not check the CLAUS organization — the first step will retry</span>
					)}
					{targetAtsUnavailable && (
						<span className="is-warn">
							! Could not read the target’s ATS settings from here — the config step may need the offline
							seed-config fallback
						</span>
					)}
				</div>
			)}

			{collision && !adopted && (
				<Alert
					type="warning"
					showIcon
					style={{ marginBottom: 12 }}
					message={`“${collision.name}” already exists on the target`}
					description={
						<>
							A previous promote may have created it. Rename the copy below, or continue against the existing
							integration (webhooks and config will be applied to it — no second copy is made).
							<div style={{ marginTop: 8 }}>
								<Button size="small" onClick={() => flow.adoptExisting(collision.id)}>
									Continue against the existing copy
								</Button>
							</div>
						</>
					}
				/>
			)}

			<div className="ei-field">
				<label className="ei-field-label">New integration name</label>
				<Input value={name} onChange={(e) => setName(e.target.value)} disabled={adopted} />
			</div>

			<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
				<Switch checked={copyCredentials} onChange={setCopyCredentials} disabled={adopted} />
				<span className="js-muted">Copy credentials into the duplicate (server-side; recommended for promotion)</span>
			</div>

			{targetState && !targetState.loading && !targetAtsUnavailable && (
				<div className="ei-field">
					<label className="ei-field-label">Target ATS entry (where the job-sync config lands)</label>
					{targetEntries.length === 0 ? (
						<Alert
							type="warning"
							showIcon
							message="The target company has no ATS entries"
							description="Create the ATS integration on the target first (Settings → ATS) — the promote flow never auto-creates it. The other steps can still run; the config step will fail until the entry exists."
						/>
					) : (
						<Select
							value={targetAtsName}
							onChange={(v) => {
								setTargetAtsName(v);
								setAcks({ overwrite: false, shadowed: false });
							}}
							placeholder="Pick the target entry"
							style={{ width: '100%' }}
							options={targetEntries.map((e) => ({
								value: e.atsName,
								label: e.hasConfig ? `${e.atsName} — already has a config` : e.atsName,
							}))}
						/>
					)}
				</div>
			)}

			{sourceConfigEntries.length === 0 && sourceAts !== null && (
				<Alert
					type="info"
					showIcon
					style={{ marginBottom: 12 }}
					message="The source company has no job-sync config"
					description="The config step will fail — you can mark it as seeded offline and use seed-config.ts with a repo config file instead."
				/>
			)}
			{sourceConfigEntries.length > 1 && (
				<div className="ei-field">
					<label className="ei-field-label">Source config entry</label>
					<Select
						value={sourceAtsName}
						onChange={setSourceAtsName}
						style={{ width: '100%' }}
						options={sourceConfigEntries.map((e) => ({ value: e.atsName, label: e.atsName }))}
					/>
				</div>
			)}

			{needOverwriteAck && (
				<div style={{ marginBottom: 8 }}>
					<Checkbox checked={acks.overwrite} onChange={(e) => setAcks((a) => ({ ...a, overwrite: e.target.checked }))}>
						Replace the job-sync config already on “{targetAtsName}”.
					</Checkbox>
				</div>
			)}
			{needShadowAck && (
				<div style={{ marginBottom: 8 }}>
					<Checkbox checked={acks.shadowed} onChange={(e) => setAcks((a) => ({ ...a, shadowed: e.target.checked }))}>
						I understand “{assess?.winnerAfterWrite}” already carries a config that wins at runtime (the engine
						reads the first config-bearing entry), so the promoted config would be shadowed until that one is
						removed.
					</Checkbox>
				</div>
			)}

			{flow.phase !== 'idle' && (
				<div className="ei-promote-steps">
					<Steps direction="vertical" size="small">
						{STEP_ORDER.map((key) => {
							const step = flow.steps[key];
							return (
								<Steps.Step
									key={key}
									title={STEP_LABELS[key]}
									status={STEP_STATUS_TO_ANTD[step.status]}
									description={
										<>
											{step.status === 'manual' ? `Manual — ${step.detail}` : step.detail}
											{step.error && (
												<Alert
													type="error"
													showIcon
													style={{ marginTop: 6 }}
													message={step.error}
													description={
														key === 'duplicate' && crossCustomer ? (
															<span>
																Known CLAUS limitation: the duplicate’s target org must belong to the{' '}
																<b>same CLAUS customer</b> as the source (one API key == one customer, no
																admin bypass). Nothing was created.
															</span>
														) : key === 'config' && configFailed ? (
															<Button size="small" onClick={flow.markConfigManual}>
																Mark as seeded offline (use seed-config.ts)
															</Button>
														) : null
													}
												/>
											)}
										</>
									}
								/>
							);
						})}
					</Steps>
				</div>
			)}

			{(done || configFailed || flow.steps.config.status === 'manual') && seedCommand && (
				<div style={{ marginTop: 12 }}>
					<span className="ei-minilabel">Offline config fallback / reference</span>
					<pre className="ei-promote-cmd">{seedCommand}</pre>
					<Button size="small" onClick={downloadPromotedJson} disabled={!sourceConfigEntries.length}>
						Download promoted config JSON
					</Button>
				</div>
			)}

			{done && (
				<Alert
					type="success"
					showIcon
					style={{ marginTop: 12 }}
					message="Promotion complete"
					description={
						<>
							<ul className="ei-promote-next">
								<li>
									The copy is <b>disabled</b> — review its credentials, field mappings and schedule from the
									target company’s /integrations.
								</li>
								<li>
									The job-sync config is in <b>dry-run</b>: enable the integration and run a sync to soak —
									the engine plans against real feeds but writes nothing.
								</li>
								<li>Check the run history counts, then flip the config to Live from the target’s editor.</li>
							</ul>
							{flow.ctx.newIntegrationId && (
								<Button size="small" style={{ marginTop: 4 }} loading={enabling} onClick={enableNow}>
									Enable now (starts the sync schedule)
								</Button>
							)}
						</>
					}
				/>
			)}
		</Modal>
	);
}
